import { useState, useEffect } from 'react'
import { App } from 'antd'
import styled from 'styled-components'
import { AuthService } from '../../services/auth/authService'
import { TYPOGRAPHY, FONT_WEIGHT } from '../../styles/theme'

const StyledButton = styled.button<{ $loading: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 40px;
  padding: 0 16px;
  background: #fff;
  color: #3c4043;
  border: 1px solid #dadce0;
  border-radius: 6px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  font-family: inherit;
  cursor: ${({ $loading }) => ($loading ? 'not-allowed' : 'pointer')};
  opacity: ${({ $loading }) => ($loading ? 0.7 : 1)};
  transition:
    background 0.2s,
    box-shadow 0.2s;

  &:hover:not(:disabled) {
    background: #f8f9fa;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    background: #f1f3f4;
  }
`

const Spinner = styled.span`
  width: 18px;
  height: 18px;
  border: 2px solid #dadce0;
  border-top-color: #4285f4;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

function GoogleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0 }}
    >
      <path
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  )
}

interface GoogleSignInButtonProps {
  label?: string
}

export function GoogleSignInButton({ label = 'Continue with Google' }: GoogleSignInButtonProps) {
  const [loading, setLoading] = useState(false)
  const { notification } = App.useApp()

  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setLoading(false)
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  const handleClick = async () => {
    if (loading) return
    setLoading(true)
    const { error } = await AuthService.signInWithGoogle()
    if (error) {
      setLoading(false)
      notification.error({
        title: 'Google Sign-In Failed',
        description: error.message,
      })
    }
    // On success the browser redirects to Google — loading stays true intentionally
  }

  return (
    <StyledButton onClick={handleClick} $loading={loading} disabled={loading}>
      {loading ? <Spinner /> : <GoogleIcon />}
      {label}
    </StyledButton>
  )
}
